import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Users, Play, XCircle, CheckCircle2, Clock, AlertCircle, 
  ChevronRight, FileText, Loader2, Filter, RefreshCw, Eye,
  UserPlus, Pause, Calendar
} from 'lucide-react';
import {
  getCrewRequests,
  getCrewRequest,
  startCrewRequest,
  cancelCrewRequest,
  completeCrewRequest,
  getUsers
} from '../services/api';

const CrewRequestQueuePage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('queued,ready,started');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [startMode, setStartMode] = useState('DRAFT');
  const [windowHours, setWindowHours] = useState(8);
  const [processing, setProcessing] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCrewRequests({ status: statusFilter });
      setRequests(res.data.crew_requests || []);
    } catch (err) {
      console.error('Failed to fetch crew requests:', err);
      toast.error('Failed to load crew requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleStartRequest = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const res = await startCrewRequest(selectedRequest.id, {
        mode: startMode,
        window_hours: windowHours
      });
      toast.success('Canvass started successfully');
      setShowStartModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      console.error('Failed to start crew request:', err);
      toast.error(err.response?.data?.detail || 'Failed to start canvass');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this crew request?')) return;
    
    try {
      await cancelCrewRequest(requestId, { reason: 'Cancelled by supervisor' });
      toast.success('Crew request cancelled');
      fetchRequests();
    } catch (err) {
      console.error('Failed to cancel crew request:', err);
      toast.error(err.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const handleCompleteRequest = async (requestId) => {
    if (!window.confirm('Mark this crew request as completed?')) return;
    
    try {
      await completeCrewRequest(requestId);
      toast.success('Crew request marked as completed');
      fetchRequests();
    } catch (err) {
      console.error('Failed to complete crew request:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete request');
    }
  };

  const openDetailModal = async (request) => {
    try {
      const res = await getCrewRequest(request.id);
      setSelectedRequest(res.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Failed to fetch request details:', err);
      toast.error('Failed to load request details');
    }
  };

  const openStartModal = (request) => {
    setSelectedRequest(request);
    setStartMode(request.mode || 'DRAFT');
    setWindowHours(8);
    setShowStartModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      queued: { color: 'bg-amber-500/20 text-amber-400', icon: Clock, label: 'Queued' },
      ready: { color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle2, label: 'Ready' },
      started: { color: 'bg-green-500/20 text-green-400', icon: Play, label: 'Started' },
      completed: { color: 'bg-slate-500/20 text-slate-400', icon: CheckCircle2, label: 'Completed' },
      cancelled: { color: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Cancelled' }
    };
    const badge = badges[status] || badges.queued;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getModeBadge = (mode) => {
    if (mode === 'INTEREST') {
      return <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium">Interest</span>;
    }
    return <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">Draft</span>;
  };

  const formatCrewRequirements = (requirements) => {
    if (!requirements) return '-';
    const parts = [];
    if (requirements.crew_leader) parts.push(`${requirements.crew_leader} Lead`);
    if (requirements.mechanic_a) parts.push(`${requirements.mechanic_a} Mech A`);
    if (requirements.mechanic_b) parts.push(`${requirements.mechanic_b} Mech B`);
    if (requirements.helper) parts.push(`${requirements.helper} Helper`);
    if (requirements.construction_worker) parts.push(`${requirements.construction_worker} CW`);
    return parts.join(', ') || '-';
  };

  // Check permission
  if (!user || !['admin', 'supervisor'].includes(user.role)) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-400">Access Denied</h3>
          <p className="text-slate-400 mt-2">Only Supervisors and Admins can access the crew request queue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="crew-request-queue-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
            <Users className="w-7 h-7 text-blue-400" />
            Crew Request Queue
          </h1>
          <p className="text-slate-400 mt-1">
            Manage crew requests and start canvass windows
          </p>
        </div>
        <button
          onClick={fetchRequests}
          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 text-slate-300 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 focus:ring-2 focus:ring-blue-500"
          >
            <option value="queued,ready,started">Active (Queued, Ready, Started)</option>
            <option value="queued">Queued Only</option>
            <option value="started">Started Only</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      {/* Queue Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No crew requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Work Order</th>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Station/Division</th>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Priority</th>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Crew Size</th>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Requirements</th>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Mode</th>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Requested</th>
                  <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="text-right p-4 text-xs font-medium text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {requests.map(request => (
                  <tr key={request.id} className="hover:bg-slate-700/30">
                    <td className="p-4">
                      <div>
                        <span className="text-slate-200 font-mono text-sm">{request.wo_number || '-'}</span>
                        <p className="text-slate-400 text-sm truncate max-w-[200px]">{request.wo_title}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300 text-sm">{request.station_or_division || '-'}</span>
                    </td>
                    <td className="p-4">
                      <span className={`text-sm ${request.priority === 'high' ? 'text-red-400' : request.priority === 'medium' ? 'text-amber-400' : 'text-slate-400'}`}>
                        {request.priority || 'Normal'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-200 font-semibold">{request.crew_size_total || 0}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">{formatCrewRequirements(request.crew_requirements)}</span>
                    </td>
                    <td className="p-4">{getModeBadge(request.mode)}</td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">
                        {request.requested_at ? new Date(request.requested_at).toLocaleDateString() : '-'}
                      </span>
                    </td>
                    <td className="p-4">{getStatusBadge(request.status)}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openDetailModal(request)}
                          className="p-1.5 bg-slate-600 hover:bg-slate-500 rounded text-white"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {request.status === 'queued' && (
                          <>
                            <button
                              onClick={() => openStartModal(request)}
                              className="p-1.5 bg-green-600 hover:bg-green-700 rounded text-white"
                              title="Start Canvass"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleCancelRequest(request.id)}
                              className="p-1.5 bg-red-600 hover:bg-red-700 rounded text-white"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {request.status === 'started' && (
                          <button
                            onClick={() => handleCompleteRequest(request.id)}
                            className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white"
                            title="Mark Complete"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Start Canvass Modal */}
      {showStartModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg">
            <div className="p-6 border-b border-slate-700">
              <h3 className="text-xl font-semibold text-slate-100 flex items-center gap-2">
                <Play className="w-5 h-5 text-green-400" />
                Start Canvass
              </h3>
              <p className="text-slate-400 text-sm mt-1">
                Configure and start the canvass window
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-200 font-medium">{selectedRequest.wo_number} - {selectedRequest.wo_title}</p>
                <p className="text-slate-400 text-sm mt-1">
                  Crew Size: {selectedRequest.crew_size_total} | {formatCrewRequirements(selectedRequest.crew_requirements)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Canvass Mode</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mode"
                      value="DRAFT"
                      checked={startMode === 'DRAFT'}
                      onChange={(e) => setStartMode(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-slate-200">Draft (Direct Assignment)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mode"
                      value="INTEREST"
                      checked={startMode === 'INTEREST'}
                      onChange={(e) => setStartMode(e.target.value)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-slate-200">Interest (Bidding)</span>
                  </label>
                </div>
                <p className="text-slate-500 text-xs mt-2">
                  {startMode === 'DRAFT' 
                    ? 'Supervisors directly assign crew members to positions'
                    : 'Crew members express interest and supervisors select from pool'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Window Duration (Hours)
                </label>
                <input
                  type="number"
                  min="1"
                  max="72"
                  value={windowHours}
                  onChange={(e) => setWindowHours(parseInt(e.target.value) || 8)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-slate-500 text-xs mt-1">
                  Canvass will close automatically after this duration
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowStartModal(false); setSelectedRequest(null); }}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white font-medium"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleStartRequest}
                disabled={processing}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Start Canvass
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl my-8">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-100">Crew Request Details</h3>
              <button
                onClick={() => { setShowDetailModal(false); setSelectedRequest(null); }}
                className="p-1 hover:bg-slate-700 rounded"
              >
                <XCircle className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Work Order Info */}
              <div>
                <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Work Order</h4>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-200 font-medium">{selectedRequest.wo_number}</p>
                  <p className="text-slate-300">{selectedRequest.wo_title}</p>
                  {selectedRequest.work_order && (
                    <p className="text-slate-400 text-sm mt-1">
                      Location: {selectedRequest.work_order.location_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Request Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Status</h4>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Mode</h4>
                  {getModeBadge(selectedRequest.mode)}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Requested By</h4>
                  <p className="text-slate-300">{selectedRequest.requested_by_name}</p>
                  <p className="text-slate-500 text-sm">
                    {selectedRequest.requested_at ? new Date(selectedRequest.requested_at).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Started By</h4>
                  {selectedRequest.started_at ? (
                    <>
                      <p className="text-slate-300">{selectedRequest.started_by || 'System'}</p>
                      <p className="text-slate-500 text-sm">
                        {new Date(selectedRequest.started_at).toLocaleString()}
                      </p>
                    </>
                  ) : (
                    <p className="text-slate-500">Not started yet</p>
                  )}
                </div>
              </div>

              {/* Crew Requirements */}
              <div>
                <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Crew Requirements</h4>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4">
                    {selectedRequest.crew_requirements && Object.entries(selectedRequest.crew_requirements).map(([role, count]) => (
                      count > 0 && (
                        <div key={role} className="flex items-center justify-between">
                          <span className="text-slate-400 capitalize">{role.replace('_', ' ')}</span>
                          <span className="text-slate-200 font-semibold">{count}</span>
                        </div>
                      )
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-600 flex justify-between">
                    <span className="text-slate-300 font-medium">Total Crew Size</span>
                    <span className="text-slate-100 font-bold text-lg">{selectedRequest.crew_size_total}</span>
                  </div>
                </div>
              </div>

              {/* Audit Trail */}
              {selectedRequest.audit_trail && selectedRequest.audit_trail.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Audit Trail</h4>
                  <div className="bg-slate-700/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {selectedRequest.audit_trail.map((event, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm">
                          <span className="text-slate-500 whitespace-nowrap">
                            {new Date(event.timestamp).toLocaleString()}
                          </span>
                          <span className="text-slate-300">{event.action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedRequest.notes && (
                <div>
                  <h4 className="text-sm font-medium text-slate-400 uppercase mb-2">Notes</h4>
                  <p className="text-slate-300 bg-slate-700/50 rounded-lg p-4">{selectedRequest.notes}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              {selectedRequest.status === 'queued' && (
                <>
                  <button
                    onClick={() => { setShowDetailModal(false); openStartModal(selectedRequest); }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Start Canvass
                  </button>
                  <button
                    onClick={() => { handleCancelRequest(selectedRequest.id); setShowDetailModal(false); }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancel Request
                  </button>
                </>
              )}
              <button
                onClick={() => { setShowDetailModal(false); setSelectedRequest(null); }}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewRequestQueuePage;
